import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { apiClient } from "@/lib/api/client";
import type { User } from "@/features/accounts/types/account.types";

import { clearHijackMarker, markHijacked } from "./hijack";
import { setAccessToken } from "./token-store";

const AUTH_USER_KEY = ["auth", "user"];
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";
const PARENT_CSRF_COOKIE = "csrf_parent_refresh";

interface HijackResponse {
  access: string;
  user_id: number;
  full_name: string;
}

interface ReleaseResponse {
  access: string;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function useHijackUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const { data } = await apiClient.post<HijackResponse>(`/api/users/hijack/${userId}/`);
      return data;
    },
    onSuccess: async (data) => {
      setAccessToken(data.access);
      markHijacked(data.full_name);
      await queryClient.fetchQuery({
        queryKey: AUTH_USER_KEY,
        queryFn: async () => {
          const { data: me } = await apiClient.get<User>("/api/users/me/");
          return me;
        },
      });
    },
  });
}

export function useReleaseHijack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const csrf = readCookie(PARENT_CSRF_COOKIE);
      if (!csrf) {
        throw new Error("No active hijack session.");
      }
      // Use a bare axios call so the response interceptor's 401→refresh
      // dance doesn't kick in on this endpoint (it has its own auth via
      // the parent refresh cookie).
      const { data } = await axios.post<ReleaseResponse>(
        `${API_BASE_URL}/api/users/hijack/release/`,
        null,
        {
          withCredentials: true,
          headers: { "X-Parent-CSRF-Refresh": csrf },
        },
      );
      return data;
    },
    onSuccess: async (data) => {
      setAccessToken(data.access);
      clearHijackMarker();
      await queryClient.fetchQuery({
        queryKey: AUTH_USER_KEY,
        queryFn: async () => {
          const { data: me } = await apiClient.get<User>("/api/users/me/");
          return me;
        },
      });
    },
  });
}
